<template>
  <div>
    <slot name="label">
      <label v-if="label" class="input-label" :class="labelClasses">
        <span v-html="label"></span>
        <span v-if="required" class="text-danger"> *</span>
        <slot name="label-right"></slot>
      </label>
    </slot>
    <div class="form-group"
         :class="[
         {'input-group': hasIcon},
         {'has-danger': error},
         {'input-group-focus': focused},
         {'has-label': label || $slots.label},
         {'has-success': hasSuccess}]">

      <div v-if="addonLeftIcon || $slots.addonLeft" class="input-group-prepend">
          <span class="input-group-text" @click="$emit('addon-left-click')">
            <slot name="addonLeft">
              <i :class="addonLeftIcon"></i>
            </slot>
          </span>
      </div>
      <slot>
        <component
          v-if="component !== 'input'"
          ref="input"
          :is="component"
          v-on="listeners"
          v-bind="attributes"/>
        <input
          v-else
          ref="input"
          v-on="listeners"
          v-bind="attributes"/>
      </slot>
      <div v-if="addonRightIcon || $slots.addonRight" class="input-group-append">
            <span class="input-group-text" @click="$emit('addon-right-click')">
                <slot name="addonRight">
                    <i :class="addonRightIcon"></i>
                </slot>
            </span>
      </div>
      <slot name="infoBlock"></slot>
      <slot name="helpBlock">
        <div class="h-3 text-danger invalid-feedback block"
             :class="{'mt-1': hasIcon, 'mt-0': !hasIcon}"
        >
          <span v-if="error">
            {{error}}
          </span>
        </div>
      </slot>
    </div>
  </div>
</template>
<script>
  export default {
    inheritAttrs: false,
    name: 'fg-input',
    props: {
      required: {
        type: Boolean,
        description: 'Whether input is required (adds an asterix *)'
      },
      label: {
        type: String,
        description: 'Input label (text before input)'
      },
      error: {
        type: String,
        description: 'Input error (below input)'
      },
      labelClasses: {
        type: String,
        description: 'Input label css classes'
      },
      inputClasses: {
        type: String,
        description: 'Input css classes'
      },
      addFormControlClass: {
        type: Boolean,
        default: true
      },
      value: {
        type: [String, Number, Date, Object, Array, Boolean],
        default: '',
        description: 'Input value'
      },
      addonRightIcon: {
        type: String,
        description: 'Addon right icon'
      },
      addonLeftIcon: {
        type: String,
        description: 'Addont left icon'
      },
      component: {
        type: String,
        default: 'input',
        description: 'Inner form control component'
      }
    },
    data() {
      return {
        touched: false,
        focused: false,
        hadError: false
      }
    },
    computed: {
      listeners() {
        return {
          ...this.$listeners,
          input: this.updateValue,
          focus: this.onFocus,
          blur: this.onBlur
        }
      },
      attributes() {
        let attributes = {
          ...this.$attrs,
          value: this.value,
          'aria-describedby': 'addon-right addon-left'
        };

        if(this.addFormControlClass) {
          attributes.class = [{valid: this.value && !this.error}, this.inputClasses, this.error ? 'form-control-danger' : '', 'form-control'];
        }
        return attributes;
      },
      hasSuccess() {
        return this.hadError && this.touched && !this.error
      },
      hasIcon() {
        const { addonRight, addonLeft } = this.$slots
        return addonRight !== undefined || addonLeft !== undefined || this.addonRightIcon !== undefined || this.addonLeftIcon !== undefined
      }
    },
    methods: {
      updateValue(evt) {
        let value = evt && evt.target ? evt.target.value : evt;
        if (!this.touched && value) {
          this.touched = true
        }
        this.$emit('input', value)
      },
      onFocus(value) {
        this.focused = true;
        this.$emit('focus', value);
      },
      onBlur(value) {
        this.focused = false;
        this.$emit('blur', value);
      },
      focus() {
        let input = this.$refs.input;
        if (input && input.focus && typeof input.focus === 'function') {
          input.focus();
        }
      }
    },
    created() {
      this.$watch('error', (newVal) => {
        if(newVal){
          this.hadError = true;
        }
      }, {immediate: true})
    }
  }
</script>
<style scoped>
.input-label {
  @apply block text-sm font-medium leading-5 text-gray-700 mb-1;
}
</style>
<style>
  .editr.form-control,
  .el-date-editor.form-control{
    padding: 0 !important;
  }
  .el-date-editor.form-control {
    border: 0;
  }
</style>
